import { useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ReviewStatus, AuditStatus, resolveLocalized, type TemplateField, type AiFinding, type ReportSection, type FieldSource, type FieldSources, type ReportConfirmations, type DiagnosisCandidate, unconfirmedSections } from "@arna-med/shared";
import { resolveFieldLabel } from "@/lib/reportFields";
import { Layout } from "@/components/Layout";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2, FileDown, Loader2, RefreshCw, RotateCcw, Send } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { useReport, usePatchReport, useExportReportPdf, useRegenerateNarrative, useSetDiagnosis, useConfirmSections } from "@/hooks/api/useReports";
import { PrimaryVisitPanel } from "@/components/report/PrimaryVisitPanel";
import { SourceBadge } from "@/components/report/SourceBadge";
import { useConsultation } from "@/hooks/api/useConsultations";
import { useTemplate } from "@/hooks/api/useTemplates";
import { useSubmitForAudit, useResubmitForAudit } from "@/hooks/api/useAudit";

// KZ-SOAP section definitions (Order No. KR DSM-175/2020)
const KZ_SOAP_SECTIONS = [
  { key: "complaints", sectionLabel: "I", i18nKey: "report.kzsoap.complaints" },
  {
    key: "anamnesisDisease",
    sectionLabel: "II",
    i18nKey: "report.kzsoap.anamnesisDisease",
  },
  {
    key: "anamnesisLife",
    sectionLabel: "III",
    i18nKey: "report.kzsoap.anamnesisLife",
  },
  {
    key: "objectiveStatus",
    sectionLabel: "IV",
    i18nKey: "report.kzsoap.objectiveStatus",
  },
  {
    key: "localStatus",
    sectionLabel: "V",
    i18nKey: "report.kzsoap.localStatus",
  },
  { key: "diagnosis", sectionLabel: "VI", i18nKey: "report.kzsoap.diagnosis" },
  { key: "plan", sectionLabel: "VII", i18nKey: "report.kzsoap.plan" },
] as const;

/**
 * Renders the answer fields either plainly (no document generated) or behind a
 * collapsed accordion (document present, answers are supporting detail).
 */
function FieldsWrapper({
  collapsed,
  title,
  hint,
  children,
}: {
  collapsed: boolean;
  title: string;
  hint: string;
  children: ReactNode;
}) {
  if (!collapsed) return <>{children}</>;
  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="fields" className="border-b-0">
        <AccordionTrigger>{title}</AccordionTrigger>
        <AccordionContent className="space-y-5">
          <p className="text-xs text-muted-foreground">{hint}</p>
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export default function Report() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();

  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [generalEdits, setGeneralEdits] = useState<Record<string, string>>({});
  const [narrativeEdits, setNarrativeEdits] = useState<Record<string, string>>({});
  const [doctorNotesDraft, setDoctorNotesDraft] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  // Set when the doctor changes an answer: the text on screen was written from
  // the answers as they were, so it may no longer match them.
  const [narrativeStale, setNarrativeStale] = useState(false);

  const { data: report, isLoading: reportLoading } = useReport(id);
  const { data: consultation } = useConsultation(id);
  const { data: template } = useTemplate(consultation?.templateId);
  const patchMutation = usePatchReport(id);
  const exportPdfMutation = useExportReportPdf(id);
  const submitMutation = useSubmitForAudit(id);
  const resubmitMutation = useResubmitForAudit(id);
  const regenerateNarrativeMutation = useRegenerateNarrative(id);
  const setDiagnosisMutation = useSetDiagnosis(id);
  const confirmSectionsMutation = useConfirmSections(id);

  const handleSave = useCallback(() => {
    const mergedSpecialized = { ...(report?.editedData ?? {}), ...edits };
    const mergedGeneral = {
      ...(report?.generalData ?? {}),
      ...(report?.generalEditedData ?? {}),
      ...generalEdits,
    };
    const mergedNarrative = {
      ...(report?.narrativeEditedData ?? {}),
      ...narrativeEdits,
    };

    const hasRemainingMissing = (report?.missingFields ?? []).some((key) => {
      const val = mergedSpecialized[key] ?? report?.structuredData?.[key];
      if (typeof val === "object" && val !== null) {
        const v = val as { right?: unknown; left?: unknown };
        return !v.right || !v.left;
      }
      return !val;
    });

    patchMutation.mutate(
      {
        editedData: Object.keys(edits).length ? mergedSpecialized : undefined,
        generalEditedData: Object.keys(generalEdits).length
          ? mergedGeneral
          : undefined,
        narrativeEditedData: Object.keys(narrativeEdits).length
          ? mergedNarrative
          : undefined,
        reviewStatus: hasRemainingMissing
          ? ReviewStatus.Draft
          : ReviewStatus.Complete,
        doctorNotes: doctorNotesDraft !== null ? doctorNotesDraft : undefined,
      },
      {
        onSuccess: () => {
          setEdits({});
          setGeneralEdits({});
          setNarrativeEdits({});
          setDoctorNotesDraft(null);
          setIsDirty(false);
        },
      },
    );
  }, [report, edits, generalEdits, narrativeEdits, doctorNotesDraft, patchMutation]);

  // Auto-save 1.5 s after last edit
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isDirty || patchMutation.isPending) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(handleSave, 1500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [isDirty, handleSave, patchMutation.isPending]);

  const handleFieldEdit = (key: string, value: unknown) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
    // The answers just changed — the generated text is now behind them.
    setNarrativeStale(true);
  };

  const handleNarrativeEdit = (sectionKey: string, value: string) => {
    setNarrativeEdits((prev) => ({ ...prev, [sectionKey]: value }));
    setIsDirty(true);
  };

  const handleBilateralEdit = (
    key: string,
    side: "right" | "left",
    value: string,
  ) => {
    setEdits((prev) => {
      const existing = (prev[key] ??
        report?.editedData?.[key] ??
        report?.structuredData?.[key]) as
        | { right?: string; left?: string }
        | undefined;
      return { ...prev, [key]: { ...existing, [side]: value } };
    });
    setIsDirty(true);
    setNarrativeStale(true);
  };

  const handleGeneralEdit = (key: string, value: string) => {
    setGeneralEdits((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleDoctorNotesEdit = (value: string) => {
    setDoctorNotesDraft(value);
    setIsDirty(true);
  };

  if (reportLoading)
    return (
      <Layout>
        <Spinner />
      </Layout>
    );
  if (!report)
    return (
      <Layout>
        <p className="p-8 text-muted-foreground">Report not found.</p>
      </Layout>
    );

  // Prefer the snapshot captured on the report at generation time so template
  // edits don't retroactively change this report; fall back to the live template.
  const snapshotFields = report.templateSnapshot?.fields as
    | TemplateField[]
    | undefined;
  const templateFields =
    snapshotFields && snapshotFields.length > 0
      ? snapshotFields
      : (template?.fields as TemplateField[] | undefined);
  const mergedData = {
    ...report.structuredData,
    ...(report.editedData ?? {}),
    ...edits,
  };
  const missingSet = new Set(report.missingFields ?? []);

  const mergedGeneral = {
    ...(report.generalData ?? {}),
    ...(report.generalEditedData ?? {}),
    ...generalEdits,
  } as Record<string, string | null | undefined>;

  const hasGeneralData = report.generalData != null;

  // Recognised by the questionnaire, not by the report's contents: keying this
  // off "the report has provenance" put the panel on any report that happened
  // to carry a fieldSources entry.
  const fieldSources = (report.fieldSources ?? {}) as FieldSources;
  const confirmations = (report.confirmations ?? {}) as ReportConfirmations;
  const isPrimaryVisit = template?.isPrimaryVisit === true;
  const diagnosisCandidates = (report.diagnosisCandidates ??
    []) as DiagnosisCandidate[];
  // The same rule the backend enforces on submit, so the screen never offers a
  // button the server will refuse.
  const pendingConfirmation = isPrimaryVisit
    ? unconfirmedSections({ fieldSources, confirmations })
    : [];

  // ── The report document (Trello #87) ──
  // Sections come from the snapshot taken at generation time, so editing the
  // structure later never re-shapes an already generated report.
  const narrativeSections = ((report.reportStructureSnapshot?.sections ??
    []) as ReportSection[])
    .slice()
    .sort((a, b) => a.order - b.order);
  const mergedNarrative = {
    ...(report.narrativeData ?? {}),
    ...(report.narrativeEditedData ?? {}),
    ...narrativeEdits,
  } as Record<string, string | undefined>;
  const hasNarrative = narrativeSections.length > 0;
  // Reports generated before this feature have no sections yet; the card still
  // shows so the doctor can render the document from the existing answers.
  const canGenerateNarrative = (templateFields?.length ?? 0) > 0;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">{t("report.title")}</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportPdfMutation.mutate(i18n.language)}
              disabled={exportPdfMutation.isPending}
            >
              <FileDown className="h-4 w-4 mr-2" />
              {exportPdfMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("report.export")}
            </Button>
            {report.reviewStatus === ReviewStatus.Draft ? (
              <span className="flex items-center gap-1 text-sm text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                <AlertTriangle className="h-4 w-4" />
                {t("report.draft")}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-green-600 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
                <CheckCircle2 className="h-4 w-4" />
                {t("report.complete")}
              </span>
            )}
          </div>
        </div>

        {missingSet.size > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <p className="font-medium mb-1">{t("report.missingFieldsNote")}</p>
            <p className="text-xs">{t("report.missingFieldsHint")}</p>
          </div>
        )}

        {isPrimaryVisit && (
          <PrimaryVisitPanel
            pending={pendingConfirmation}
            confirmedCount={Object.keys(confirmations).length}
            candidates={diagnosisCandidates}
            data={mergedData}
            missingFields={report.missingFields ?? []}
            language={consultation?.language ?? i18n.language}
            // The server re-renders the document as part of setting the
            // diagnosis, so there is nothing stale to warn about here.
            onSetDiagnosis={(payload) => setDiagnosisMutation.mutate(payload)}
            onConfirmSection={(sectionKey) =>
              confirmSectionsMutation.mutate([sectionKey])
            }
            isSettingDiagnosis={setDiagnosisMutation.isPending}
            isConfirming={confirmSectionsMutation.isPending}
          />
        )}

        {/* Revision requested banner */}
        {report.auditStatus === AuditStatus.Revision && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 space-y-2">
            <p className="font-medium flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />
              {t("audit.revisionBanner")}
            </p>
            {report.auditorNotes && (
              <p className="text-xs bg-red-100 rounded p-2">{report.auditorNotes}</p>
            )}
            <Button
              size="sm"
              variant="outline"
              className="border-red-300"
              onClick={() => resubmitMutation.mutate()}
              disabled={resubmitMutation.isPending || report.reviewStatus !== ReviewStatus.Complete}
            >
              <Send className="h-3 w-3 mr-1" />
              {t("audit.resubmit")}
            </Button>
          </div>
        )}

        {/* AI Findings banner */}
        {(() => {
          const aiFindings = (report.aiFindings as AiFinding[] | null) ?? [];
          const activeFindings = aiFindings.filter((f) => !f.fixed);
          if (!aiFindings.length || report.auditStatus === AuditStatus.Approved) return null;
          // Show the localized field label instead of the raw key in findings.
          const findingLabel = (key: string) =>
            resolveFieldLabel(key, templateFields, i18n.language, t);
          return (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm space-y-2">
              <p className="font-medium text-yellow-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {t("audit.aiFindingsTitle")} ({activeFindings.length} {activeFindings.length !== aiFindings.length ? `/ ${aiFindings.length} total` : ""})
              </p>
              <div className="space-y-1">
                {aiFindings.map((f, i) => (
                  <div key={i} className={`flex items-start gap-2 text-xs rounded p-2 ${f.fixed ? "opacity-50 line-through bg-green-50" : f.severity === "error" ? "bg-red-50 text-red-700" : "bg-yellow-100 text-yellow-800"}`}>
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span><strong>{findingLabel(f.field)}:</strong> {f.issue}{f.fixed ? ` ✓ ${t("audit.aiFixed")}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Submit for audit button */}
        {report.auditStatus === AuditStatus.NotSubmitted && report.reviewStatus === ReviewStatus.Complete && (
          <div className="flex justify-end items-center gap-3">
            {pendingConfirmation.length > 0 && (
              <p className="text-xs text-amber-700">
                {t("primaryVisit.submitBlocked")}
              </p>
            )}
            <Button
              onClick={() => submitMutation.mutate()}
              disabled={
                submitMutation.isPending ||
                isDirty ||
                pendingConfirmation.length > 0
              }
            >
              <Send className="h-4 w-4 mr-2" />
              {t("audit.submitForAudit")}
            </Button>
          </div>
        )}

        {/* Approved badge */}
        {report.auditStatus === AuditStatus.Approved && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {t("audit.approved")}
          </div>
        )}

        {/* ── Report document: the structure's sections as paragraphs ──
            Not on a primary visit: there the answers above ARE the document,
            and the PDF prints those. A second, separately worded text under the
            same headings is what made the export disagree with the screen
            (Trello #152). */}
        {!isPrimaryVisit && (hasNarrative || canGenerateNarrative) && (
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">
                {t("report.documentTitle")}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  regenerateNarrativeMutation.mutate(undefined, {
                    onSuccess: () => {
                      setNarrativeEdits({});
                      setNarrativeStale(false);
                    },
                  });
                }}
                disabled={regenerateNarrativeMutation.isPending}
              >
                {regenerateNarrativeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {t("report.regenerateNarrative")}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {narrativeStale && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                  {t("report.narrativeStale")}
                </div>
              )}
              {!hasNarrative && (
                <p className="text-sm text-muted-foreground">
                  {t("report.narrativeEmpty")}
                </p>
              )}
              {narrativeSections.map((section) => {
                const text = mergedNarrative[section.key] ?? "";
                return (
                  <div key={section.key}>
                    <p className="font-semibold text-sm mb-1.5">
                      {resolveLocalized(section.title, i18n.language)}
                    </p>
                    <Textarea
                      value={text}
                      onChange={(e) =>
                        handleNarrativeEdit(section.key, e.target.value)
                      }
                      placeholder="—"
                      className="text-sm resize-y"
                      // Grow with the paragraph: a document that is cut off at
                      // three lines is not a document.
                      rows={Math.min(
                        24,
                        Math.max(
                          2,
                          text.split("\n").length +
                            Math.ceil(text.length / 90),
                        ),
                      )}
                    />
                  </div>
                );
              })}
              {hasNarrative && (
                <p className="text-xs text-muted-foreground">
                  {t("report.narrativeHint")}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── KZ-SOAP General Record (Order No. KR DSM-175/2020) ── */}
        {hasGeneralData && (
          <Card>
            <CardContent className="space-y-4">
              {KZ_SOAP_SECTIONS.map(({ key, sectionLabel, i18nKey }) => {
                const rawValue = mergedGeneral[key];
                const editVal = generalEdits[key];
                const displayValue = editVal ?? rawValue ?? "";
                return (
                  <div key={key}>
                    <p className="font-semibold text-sm mb-1.5">
                      <span className="inline-flex items-center justify-center w-7 h-5 text-xs bg-primary/10 text-primary rounded mr-2 font-mono">
                        {sectionLabel}
                      </span>
                      {t(i18nKey)}
                    </p>
                    <Textarea
                      value={displayValue}
                      onChange={(e) => handleGeneralEdit(key, e.target.value)}
                      placeholder="—"
                      className="text-sm min-h-[56px] resize-y"
                      rows={2}
                    />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* ── Specialized Template: the answers behind the document ── */}
        <Card>
          {hasGeneralData && !hasNarrative && (
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {t("report.specializedTitle")}
              </CardTitle>
            </CardHeader>
          )}
          {/* With a document on screen the raw answers are the supporting
              detail, so they collapse — a 118-field template is unreadable
              expanded — but stay one click away and fully editable. */}
          <CardContent className={hasNarrative ? "pt-2 pb-0" : "pt-6 space-y-5"}>
            <FieldsWrapper
              collapsed={hasNarrative}
              title={t("report.structuredDataTitle", {
                count: templateFields?.length ?? 0,
              })}
              hint={t("report.structuredDataHint")}
            >
            {templateFields
              ? templateFields.map((field) => {
                  const isMissing = missingSet.has(field.key);
                  const value = mergedData[field.key];
                  const fieldLabel = resolveLocalized(field.label, i18n.language);

                  return (
                    <div
                      key={field.key}
                      className={
                        isMissing
                          ? "border-l-4 border-amber-400 pl-3"
                          : undefined
                      }
                    >
                      <p className="font-semibold text-sm mb-1">
                        {fieldLabel}
                        <SourceBadge
                          source={fieldSources[field.key] as FieldSource | undefined}
                        />
                        {isMissing && (
                          <span className="ml-2 text-xs text-amber-600 font-normal">
                            {t("report.missingField")}
                          </span>
                        )}
                      </p>
                      {field.bilateral ? (
                        <div className="grid grid-cols-2 gap-3">
                          {(["right", "left"] as const).map((side) => {
                            const sideVal = (
                              (value ?? {}) as Record<string, string>
                            )[side];
                            const editVal = (
                              (edits[field.key] ?? {}) as Record<string, string>
                            )[side];
                            return (
                              <div key={side}>
                                <p className="text-xs text-muted-foreground mb-1 capitalize">
                                  {t(`report.${side}`)}
                                </p>
                                <Input
                                  value={editVal ?? sideVal ?? ""}
                                  onChange={(e) =>
                                    handleBilateralEdit(
                                      field.key,
                                      side,
                                      e.target.value,
                                    )
                                  }
                                  placeholder={`${t(`report.${side}`)}...`}
                                  className="text-sm"
                                />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <Textarea
                          value={
                            (edits[field.key] as string | undefined) ??
                            (value as string | null | undefined) ??
                            ""
                          }
                          onChange={(e) =>
                            handleFieldEdit(field.key, e.target.value)
                          }
                          placeholder={`${fieldLabel}...`}
                          className="text-sm min-h-[56px] resize-y"
                          rows={2}
                        />
                      )}
                    </div>
                  );
                })
              : Object.entries(mergedData).map(([key, value]) => (
                  <div key={key}>
                    <p className="font-semibold text-sm capitalize mb-1">
                      {key.replace(/([A-Z])/g, " $1")}
                    </p>
                    <Textarea
                      value={String(value ?? "")}
                      onChange={(e) => handleFieldEdit(key, e.target.value)}
                      className="text-sm min-h-[56px] resize-y"
                      rows={2}
                    />
                  </div>
                ))}
            </FieldsWrapper>
          </CardContent>
          {(isDirty || patchMutation.isPending) && (
            <CardFooter className="border-t pt-3 pb-3">
              <p className="text-xs text-muted-foreground">
                {patchMutation.isPending && (
                  <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
                )}
                {t("report.autoSaving")}
              </p>
            </CardFooter>
          )}
        </Card>

        {/* Doctor Notes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("report.doctorNotes")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={doctorNotesDraft !== null ? doctorNotesDraft : (report.doctorNotes ?? "")}
              onChange={(e) => handleDoctorNotesEdit(e.target.value)}
              placeholder={t("report.doctorNotesPlaceholder")}
              className="text-sm min-h-[100px] resize-y"
              rows={4}
            />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
